import {
  catchErrorInto,
  effect,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  Service,
  smartRetry,
} from '@toeverything/infra';
import type { Subscription } from 'rxjs';
import { EMPTY, exhaustMap, tap } from 'rxjs';

import type { WorkspaceService } from '../../workspace';
import type { Member } from '../entities/members';
import type { MemberSearchStore } from '../stores/member-search';

export class MemberSearchService extends Service {
  constructor(
    private readonly store: MemberSearchStore,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
    this.subscription = this.store
      .subscribeMembers(this.workspaceService.workspace.id)
      .subscribe({
        next: () => {
          if (this.searchText$.value) {
            this.search(this.searchText$.value);
          }
        },
        error: error => this.error$.setValue(error),
      });
  }

  private readonly subscription: Subscription;

  readonly PAGE_SIZE = 8;
  readonly searchText$ = new LiveData<string>('');
  readonly isLoading$ = new LiveData(false);
  readonly error$ = new LiveData<any>(null);
  readonly result$ = new LiveData<Member[]>([]);
  readonly hasMore$ = new LiveData(true);

  readonly loadMore = effect(
    exhaustMap(() => {
      if (!this.hasMore$.value) {
        return EMPTY;
      }
      return fromPromise(async signal => {
        return this.store.getMembersByEmailOrName(
          this.workspaceService.workspace.id,
          this.searchText$.value || undefined,
          this.result$.value.length,
          this.PAGE_SIZE,
          signal
        );
      }).pipe(
        tap(data => {
          this.result$.setValue([...this.result$.value, ...data.members]);
          this.hasMore$.setValue(data.members.length === this.PAGE_SIZE);
        }),
        smartRetry(),
        catchErrorInto(this.error$),
        onStart(() => {
          this.isLoading$.setValue(true);
        }),
        onComplete(() => this.isLoading$.setValue(false))
      );
    })
  );

  /** Se a primeira página já foi pedida nesta sessão do menu. Ver `garantirListaInicial`. */
  private pediuListaInicial = false;

  reset() {
    this.pediuListaInicial = false;
    this.result$.setValue([]);
    this.hasMore$.setValue(true);
    this.searchText$.setValue('');
    this.error$.setValue(null);
    this.loadMore.reset();
  }

  search(searchText?: string) {
    this.reset();
    this.searchText$.setValue(searchText ?? '');
    this.pediuListaInicial = true;
    this.loadMore();
  }

  /**
   * Traz a primeira página de membros SEM que ninguém tenha digitado nada.
   *
   * ## Por que isto precisou existir
   *
   * O menu do `@` monta o grupo "Mention Members" a partir de `result$`, e o
   * `AtMenuConfigService` só chamava `search(query)` quando `query.length > 0`. Com o `@`
   * recém-digitado a consulta é vazia, nenhuma busca saía, `result$` ficava `[]` — e o grupo
   * se auto-ocultava (o `hidden` dele) porque não tinha itens. O popover então nem o
   * desenhava.
   *
   * Resultado medido no `dev` em 2026-08-28: `@` sozinho não listava membro nenhum, e `@v`
   * listava `victorgaudio` na hora. Falha silenciosa completa — sem erro, sem mensagem, sem
   * nada que dissesse que faltava digitar. Card 1851768965949294521.
   *
   * ## Por que uma trava, e não `if (result$.length === 0)`
   *
   * O grupo é remontado a cada tecla e a cada re-render do popover. Condicionar ao `result$`
   * vazio faria um espaço sem membro nenhum re-disparar a busca para sempre — e um laço que
   * só acontece no caso raro é o pior formato de laço, porque passa em todo teste.
   *
   * A trava é levantada por `reset()`, que é o que `search()` chama: uma busca de verdade
   * invalida a lista inicial, e a próxima volta ao `@` vazio a pede de novo.
   */
  garantirListaInicial() {
    if (this.isLoading$.value) return;
    // Já pedida E a lista em mãos é a da consulta vazia: não há o que fazer.
    if (this.pediuListaInicial && this.searchText$.value === '') return;
    // Chega aqui em dois casos: nunca pediu, ou a lista em mãos é o resultado de um termo
    // que a pessoa acabou de apagar. O segundo importa: sem ele, apagar `@victor` de volta
    // para `@` deixaria na tela os membros que casavam com "victor" — ou, se o termo não
    // casasse com ninguém, uma lista vazia que parece "este espaço não tem membros".
    this.search();
  }

  override dispose(): void {
    this.loadMore.unsubscribe();
    this.subscription.unsubscribe();
  }
}
